import { useTranslation } from 'react-i18next';
import Form from 'antd/es/form';
import React, { useCallback, useEffect, useState } from 'react';
import opConfig from 'openpathConfig';
import { useLocation } from 'react-router';

import { getAccessToken } from 'utils/accessToken';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { checkAuthIsUPCPartnerOrg } from 'utils/checkAuthIsUPCPartnerOrg';
import { checkIsVideoOnlyCustomer } from 'utils/checkIsVideoOnlyCustomer';
import { checkMasterModeAccess } from 'new-components/OrgSwitcher/helpers/orgAccess';
import { FormOnSubmitType, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpTextButton } from 'new-components/DLS/OpTextButton/OpTextButton';
import { setWindowLocation } from 'utils/setWindowLocation';

import { hasOnlyMailroomScopes } from '../../helpers/hasOnlyMailroomScopes';
import {
  PossibleLogins,
  SelectedLogin,
} from '../PossibleLogins/PossibleLogins';
import { Logins } from '../../helpers/getPossibleLogins';
import { useGetLoginsForEmail } from '../../hooks/useGetLoginsForEmail';
import { useAltaLoginUrlParams } from '../../hooks/useAltaLoginUrlParams';
import { HandleIdpSsoLoginArgs } from '../../types/ssoLogin';
import { isValidAbsoluteRedirectUrl } from '../../helpers/isValidAbsoluteRedirectUrl';
import { redirectToUpc } from '../../helpers/redirectToUpc';
import { redirectToAccess } from '../../helpers/redirectToAccess';
import {
  getVideoOnlyCustomerRedirectUrl,
  TokenScopeOrg,
} from '../../helpers/getVideoOnlyCustomerRedirectUrl';
import { isValidRelativePath } from '../../helpers/isValidRelativeUrl';
import { redirectToMailroom } from '../../helpers/redirectToMailroom';
import { isVideoOnlyUser } from '../../helpers/isVideoOnlyUser';

import { PasswordForm } from './PasswordForm';
import { EmailForm, EmailFormInstance } from './EmailForm';

import './LoginFlow.scss';

interface LoginFlowProps {
  initialEmail: string | null;
  onForgotPassword(value: { didForget: boolean; initialEmail: string }): void;
  handleIdpSsoLogin(args: HandleIdpSsoLoginArgs): void;
}

/**
 * @component
 * @description Component that allows the user to login
 * @param {string} props.initialEmail - email currently being used - useful if navigating back here from a
 * different route like forgotPassword.
 * @param {string} props.onForgotPassword - callback to navigate to forgot password route.
 */
export const LoginFlow = ({
  initialEmail,
  onForgotPassword,
  handleIdpSsoLogin,
}: LoginFlowProps) => {
  const { t } = useTranslation();
  const { orgId, redirectUrl, emailParam, service } = useAltaLoginUrlParams();

  const [emailForm] = OpForm.useForm<EmailFormInstance>();
  const accessToken = getAccessToken();
  const location = useLocation();

  // prioritize initialEmail prop when first mounting this component.
  // use the email search param if the initialEmail is not set.
  const [email, setEmail] = React.useState(initialEmail ?? emailParam);
  const [logins, setLogins] = React.useState<Logins[] | undefined>();
  const [selectedLogin, setSelectedLogin] = useState<
    SelectedLogin | undefined
  >();

  const resetLogins = useCallback(() => {
    setLogins(undefined);
    setSelectedLogin(undefined);
    setEmail(null);
  }, []);

  const { getLoginsForEmail, isSubmittingEmail } = useGetLoginsForEmail({
    emailForm,
    setLogins,
    setSelectedLogin,
    handleIdpSsoLogin,
  });

  const onEmailSubmit: FormOnSubmitType = useCallback(
    async ({ values }) => {
      setEmail(values.email);
      await getLoginsForEmail(values.email);
    },
    [setEmail, getLoginsForEmail],
  );

  const { data: tokenData } = useOpQuery({
    apiEndpointName: 'describeAccessToken',
    parameters: [accessToken],
    enabled: Boolean(accessToken),
    select: (data) => data.json.data,
  });

  const shouldRedirectToCustomUrl = useCallback(() => {
    return (
      redirectUrl &&
      (isValidAbsoluteRedirectUrl(redirectUrl) ||
        isValidRelativePath(redirectUrl))
    );
  }, [redirectUrl]);

  const getRedirectRegion = useCallback((): OP.Env => {
    return (
      opConfig.LOGIN_REDIRECT_REGION_OVERRIDE ||
      selectedLogin?.regionEnv ||
      opConfig.ENV
    );
  }, [selectedLogin?.regionEnv]);

  const findTokenScopeListItem = useCallback(
    (token: Api.Response['describeAccessToken']) => {
      const matchingScope = token.tokenScopeList?.find((tokenScope) =>
        orgId
          ? tokenScope.org?.id === Number(orgId)
          : checkAuthIsUPCPartnerOrg(tokenScope),
      );

      if (matchingScope) return matchingScope;

      return token.tokenScopeList?.find((tokenScope) => tokenScope.org?.id);
    },
    [orgId],
  );

  const handleTokenScopeRedirect = useCallback(
    (
      tokenScopeListItem: Utils.ArrayElement<
        Api.Response['describeAccessToken']['tokenScopeList']
      >,
      region: OP.Env,
    ) => {
      if (checkAuthIsUPCPartnerOrg(tokenScopeListItem)) {
        const queryParams = service === 'dmp' ? { service: 'dmp' } : null;
        redirectToUpc(region, tokenScopeListItem.org?.id, queryParams);
        return;
      }

      if (shouldRedirectToCustomUrl()) {
        setWindowLocation(redirectUrl);
        return;
      }

      if (
        checkIsVideoOnlyCustomer(tokenScopeListItem.org?.orgNativeApps) ||
        isVideoOnlyUser(tokenScopeListItem)
      ) {
        const videoUrl = getVideoOnlyCustomerRedirectUrl({
          ...tokenScopeListItem.org,
          isUnified: tokenScopeListItem.org?.isUnified ?? false,
        } as TokenScopeOrg);

        setWindowLocation(videoUrl!);
        return;
      }

      if (hasOnlyMailroomScopes(tokenScopeListItem)) {
        redirectToMailroom(region, tokenScopeListItem.org?.id);
        return;
      }

      /**
       * A couple notes:
       *
       * - This component can render on the `/login` route
       * - The `redirectToAccess` function will redirect '/'
       * - The root route, in some scenarios, redirects to `/login`
       *
       * Given the above, there can be a redirect loop; to ensure that does
       * not happen - prevent the redirect if the user is on the `/login`
       * route.
       */
      if (location.pathname.includes('login')) return;
      redirectToAccess(region);
    },
    [service, location.pathname, shouldRedirectToCustomUrl, redirectUrl],
  );

  // verify of the user is already logged in and redirect them to the correct page
  useEffect(() => {
    if (!tokenData) return;

    const region = getRedirectRegion();

    const hasMasterModeAccess = checkMasterModeAccess({ token: tokenData });
    if (hasMasterModeAccess) {
      redirectToAccess(region, 'master');
      return;
    }

    const tokenScopeListItem = findTokenScopeListItem(tokenData);
    if (!tokenScopeListItem) return;

    handleTokenScopeRedirect(tokenScopeListItem, region);
  }, [
    tokenData,
    shouldRedirectToCustomUrl,
    getRedirectRegion,
    findTokenScopeListItem,
    handleTokenScopeRedirect,
    redirectUrl,
  ]);

  // Only do on mount if emailParam is set
  // "Not You?" button resets email state and allows the user to retype their email whether the email param is set or not
  useEffect(() => {
    const emailToFetch = initialEmail ?? emailParam;
    if (emailToFetch) {
      getLoginsForEmail(emailToFetch);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // track the email as it is typed
  const currentEmailValue = Form.useWatch('email', emailForm);

  const handleForgotPassword = () => {
    onForgotPassword({
      didForget: true,
      initialEmail: currentEmailValue ?? email,
    });
  };

  // Should only show one
  const needsEmail = !logins && !selectedLogin;
  const needsNamespace = logins && email && !selectedLogin;
  const needsPassword = email && selectedLogin;

  return (
    <div>
      {needsEmail && (
        <EmailForm
          form={emailForm}
          isSubmitting={isSubmittingEmail}
          initialValues={{ email }}
          onSubmit={onEmailSubmit}
        />
      )}
      {needsNamespace && (
        <PossibleLogins
          email={email}
          logins={logins}
          resetLogins={resetLogins}
          setSelectedLogin={setSelectedLogin}
          handleIdpSsoLogin={handleIdpSsoLogin}
        />
      )}
      {needsPassword && (
        <PasswordForm
          email={email}
          namespace={selectedLogin.namespace}
          regionEnv={selectedLogin.regionEnv}
          heliumEndpoint={selectedLogin.heliumEndpoint}
          resetLogins={resetLogins}
        />
      )}
      <OpTextButton className="forgot-password" onClick={handleForgotPassword}>
        {t('Forgot password?')}
      </OpTextButton>
    </div>
  );
};
